import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../i18n';
import { api, profileApi, prayerApi } from '../api/client';
import { List, Sparkles } from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import './GettingStartedScreen.css';

// ── Relationship definitions — value = English canonical (stored in DB) ────────
const RELATIONSHIP_DEFS = [
  // Immediate Family
  { value:'Husband',        group:'immediate', single:true  },
  { value:'Wife',           group:'immediate', single:true  },
  { value:'Fiancé',         group:'immediate', single:true  },
  { value:'Mother',         group:'immediate', single:true  },
  { value:'Step Mother',    group:'immediate', single:true  },
  { value:'Father',         group:'immediate', single:true  },
  { value:'Step Father',    group:'immediate', single:true  },
  { value:'Son',            group:'immediate', single:false },
  { value:'Step Son',       group:'immediate', single:false },
  { value:'Daughter',       group:'immediate', single:false },
  { value:'Step Daughter',  group:'immediate', single:false },
  { value:'Brother',        group:'immediate', single:false },
  { value:'Step Brother',   group:'immediate', single:false },
  { value:'Sister',         group:'immediate', single:false },
  { value:'Step Sister',    group:'immediate', single:false },
  // Extended Family
  { value:'Grandmother',    group:'extended',  single:true  },
  { value:'Grandfather',    group:'extended',  single:true  },
  { value:'Mother-in-law',  group:'extended',  single:true  },
  { value:'Father-in-law',  group:'extended',  single:true  },
  { value:'Cousin',         group:'extended',  single:false },
  { value:'Uncle',          group:'extended',  single:false },
  { value:'Aunt',           group:'extended',  single:false },
  { value:'Nephew',         group:'extended',  single:false },
  { value:'Niece',          group:'extended',  single:false },
  { value:'Grandson',       group:'extended',  single:false },
  { value:'Granddaughter',  group:'extended',  single:false },
  { value:'Brother-in-law', group:'extended',  single:false },
  { value:'Sister-in-law',  group:'extended',  single:false },
  { value:'Son-in-law',     group:'extended',  single:false },
  { value:'Daughter-in-law',group:'extended',  single:false },
  // Friends & Others
  { value:'Boyfriend',      group:'friends',   single:true  },
  { value:'Girlfriend',     group:'friends',   single:true  },
  { value:'Employer',       group:'friends',   single:true  },
  { value:'Best Friend',    group:'friends',   single:false },
  { value:'Close Friend',   group:'friends',   single:false },
  { value:'Friend',         group:'friends',   single:false },
  { value:'Neighbor',       group:'friends',   single:false },
  { value:'Co-worker',      group:'friends',   single:false },
  { value:'Employee',       group:'friends',   single:false },
  { value:'Acquaintance',   group:'friends',   single:false },
  { value:'Stranger',       group:'friends',   single:false },
  { value:'Church Family',  group:'friends',   single:false },
  { value:'Classmate',      group:'friends',   single:false },
  { value:'Pastor',         group:'friends',   single:false },
  { value:'Mentor',         group:'friends',   single:false },
  { value:'Mentee',         group:'friends',   single:false },
];

// English canonical array in the same order as prayer.relationships locale arrays
const EN_RELATIONSHIPS = [
  'Husband','Wife','Fiancé','Mother','Step Mother','Father','Step Father',
  'Brother','Step Brother','Sister','Step Sister','Son','Step Son','Daughter','Step Daughter',
  'Cousin','Uncle','Aunt','Niece','Nephew','Grandson','Granddaughter',
  'Grandmother','Grandfather','Mother-in-law','Father-in-law','Brother-in-law',
  'Sister-in-law','Son-in-law','Daughter-in-law',
  'Boyfriend','Girlfriend','Best Friend','Close Friend','Friend',
  'Neighbor','Employee','Employer','Co-worker','Acquaintance',
  'Stranger','Church Family','Classmate','Pastor','Mentor','Mentee',
];

const GROUP_META = {
  immediate: { label:'Immediate Family', color:'#1B4F8A', darkBg:'rgba(27,79,138,0.1)'  },
  extended:  { label:'Extended Family',  color:'#0e9a8a', darkBg:'rgba(14,154,138,0.1)' },
  friends:   { label:'Friends & Others', color:'#6366f1', darkBg:'rgba(99,102,241,0.1)' },
};

// ── Platform detection ────────────────────────────────────────────────────────
function detectPlatform() {
  const ua = navigator.userAgent;
  const isIOS     = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const isAndroid = /Android/.test(ua);
  return { isIOS, isAndroid, isMobile: isIOS || isAndroid };
}

// ── Timezones / time helpers ──────────────────────────────────────────────────
const TIMEZONES = [
  { label:'US & Canada',    zones:['America/New_York','America/Chicago','America/Denver','America/Los_Angeles','America/Anchorage','Pacific/Honolulu','America/Phoenix'] },
  { label:'Europe',         zones:['Europe/London','Europe/Paris','Europe/Berlin','Europe/Rome','Europe/Madrid','Europe/Amsterdam'] },
  { label:'Asia / Pacific', zones:['Asia/Tokyo','Asia/Seoul','Asia/Shanghai','Asia/Hong_Kong','Asia/Singapore','Asia/Kolkata','Asia/Dubai','Australia/Sydney','Pacific/Auckland'] },
  { label:'Latin America',  zones:['America/Sao_Paulo','America/Mexico_City','America/Bogota','America/Lima','America/Santiago'] },
  { label:'Other',          zones:['Africa/Johannesburg','America/Toronto','America/Vancouver'] },
];
function tzLabel(tz) { return tz.split('/').pop().replace(/_/g,' '); }
const TIME_OPTIONS = [];
for (let h = 0; h < 24; h++) {
  for (let m = 0; m < 60; m += 30) {
    const hh=String(h).padStart(2,'0'), mm=String(m).padStart(2,'0');
    const label=h===0?`12:${mm} AM`:h<12?`${h}:${mm} AM`:h===12?`12:${mm} PM`:`${h-12}:${mm} PM`;
    TIME_OPTIONS.push({ value:`${hh}:${mm}`, label });
  }
}
// Auto-detect timezone behind the scenes; fall back gracefully.
function detectTimezone() {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York'; }
  catch { return 'America/New_York'; }
}
// Human clock label for a stored HH:mm value (e.g. '06:00' → '6:00 AM').
function clockLabel(value) {
  const opt = TIME_OPTIONS.find(o => o.value === value);
  return opt ? opt.label : value;
}

// Poetic reminder hours → real HH:mm stored in prayerNotificationTime.
const HOURS = [
  { key:'dawn',    value:'06:00' },
  { key:'morning', value:'07:30' },
  { key:'midday',  value:'12:00' },
  { key:'dusk',    value:'18:00' },
  { key:'night',   value:'21:00' },
];

// Inside the native (Capacitor) app there's nothing to install, so the final
// "Keep the flame close" moment is skipped — see `showInstall` in the main
// component. `middle` (the progress dots) is computed from that flag.
function isNativeApp() {
  try { return Capacitor.isNativePlatform(); } catch { return false; }
}

function prefersReducedMotion() {
  return typeof window !== 'undefined' && window.matchMedia
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

// ── Transition-based reveal ────────────────────────────────────────────────────
// Entrance fires from a setTimeout mount flag (NOT requestAnimationFrame — rAF is
// paused when the page/tab is offscreen, which can strand content at opacity:0).
// End state is always opacity:1. Reduced-motion users see content immediately.
function Reveal({ dur = 800, y = 12, delay = 0, className = '', style = {}, children }) {
  const reduce = prefersReducedMotion();
  const [on, setOn] = useState(reduce);
  useEffect(() => {
    if (reduce) return undefined;
    const id = setTimeout(() => setOn(true), 30);
    return () => clearTimeout(id);
  }, [reduce]);
  return (
    <div className={className} style={{
      opacity: on ? 1 : 0,
      transform: on ? 'translateY(0)' : `translateY(${y}px)`,
      transition: reduce ? 'none'
        : `opacity ${dur}ms cubic-bezier(.25,.8,.3,1) ${delay}ms, transform ${dur}ms cubic-bezier(.25,.8,.3,1) ${delay}ms`,
      ...style,
    }}>{children}</div>
  );
}

// ── The candle flame (pure CSS) ────────────────────────────────────────────────
function Flame({ size = 16 }) {
  return (
    <span className="fl-flame" style={{ width: size, height: size * 1.55 }} aria-hidden="true">
      <span className="fl-flame-core" />
      <span className="fl-flame-base" style={{ width: size * 0.32, height: size * 0.5 }} />
    </span>
  );
}

// ── Drifting embers (ambient background) ───────────────────────────────────────
function Embers() {
  const parts = useMemo(() => Array.from({ length: 14 }, (_, i) => ({
    left: ((i * 53 + 11) % 96) + 2,
    size: 1.5 + ((i * 7) % 4),
    dur: 7 + ((i * 13) % 9),
    delay: -((i * 17) % 12),
    drift: (((i * 29) % 40) - 20),
  })), []);
  return (
    <div className="fl-embers" aria-hidden="true">
      {parts.map((p, i) => (
        <span key={i} className="fl-ember" style={{
          left: `${p.left}%`, width: p.size, height: p.size,
          animationDuration: `${p.dur}s`, animationDelay: `${p.delay}s`,
          ['--fl-drift']: `${p.drift}px`,
        }} />
      ))}
    </div>
  );
}

function BackArrow({ onBack, label }) {
  return (
    <button type="button" className="fl-back" onClick={onBack} aria-label={label}>‹</button>
  );
}

// ── 1. Breathe ─────────────────────────────────────────────────────────────────
function Breath({ onNext }) {
  const { t } = useTranslation('gettingStarted');
  const reduce = prefersReducedMotion();
  const [beat, setBeat] = useState(reduce ? 2 : 0);
  const title = t('breathTitle');
  const lines = Array.isArray(title) ? title : ['Before we begin,', 'take a breath.'];
  useEffect(() => {
    if (reduce) return undefined;
    const t1 = setTimeout(() => setBeat(1), 1900);
    const t2 = setTimeout(() => setBeat(2), 3700);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [reduce]);
  // The verse/hint are always rendered so the centered column's height stays
  // fixed — they fade and rise in place instead of shoving the headline upward
  // (which read as a "jump") each time a timed block mounted.
  const fade = (show) => ({
    opacity: show ? 1 : 0,
    transform: show ? 'translateY(0)' : 'translateY(10px)',
    transition: reduce ? 'none'
      : 'opacity 900ms cubic-bezier(.25,.8,.3,1), transform 900ms cubic-bezier(.25,.8,.3,1)',
  });
  return (
    <button type="button" className="fl-breath" onClick={onNext}>
      <div className="fl-rings">
        <span className="fl-ring" />
        <span className="fl-ring fl-ring-inner" />
        <Flame size={30} />
      </div>
      <h1 className="fl-breath-title">{lines[0]}<br />{lines[1]}</h1>
      <div style={{ marginTop: 30, ...fade(beat >= 1) }}>
        <p className="fl-verse">{t('breathVerse')}</p>
        <div className="fl-eyebrow">{t('breathVerseRef')}</div>
      </div>
      <div style={{ marginTop: 42, ...fade(beat >= 2) }}>
        <span className="fl-breath-hint">{t('breathHint')}</span>
      </div>
    </button>
  );
}

// ── 2. Name those you carry ──────────────────────────────────────────────────--
// Lay the recipients out as a constellation around the center "you" node.
// Pure function in a 360×360 viewBox so the SVG scales/letterboxes responsively
// without any DOM measurement. Nodes fill concentric rings (inner first), with
// alternate rings offset a half-step so the spokes never line up.
const CONST_CX = 180;
const CONST_CY = 180;
function computeConstellation(n) {
  if (n <= 0) return [];
  const caps = [6, 10, 14, 18];
  const rings = [];
  let remaining = n;
  let k = 0;
  while (remaining > 0) {
    const cap = caps[k] ?? (caps[caps.length - 1] + 4 * (k - caps.length + 1));
    const count = Math.min(cap, remaining);
    rings.push(count);
    remaining -= count;
    k++;
  }
  const ringCount = rings.length;
  const Rmax = 150;
  const Rmin = ringCount === 1 ? 104 : 86;
  const scale = n <= 8 ? 0.9 : n <= 18 ? 0.76 : n <= 32 ? 0.64 : 0.54;
  const out = [];
  rings.forEach((count, ri) => {
    const R = ringCount === 1 ? Rmin : Rmin + (Rmax - Rmin) * (ri / (ringCount - 1));
    const offset = (ri % 2) * (Math.PI / count);
    for (let i = 0; i < count; i++) {
      const angle = -Math.PI / 2 + offset + i * ((2 * Math.PI) / count);
      out.push({ x: CONST_CX + R * Math.cos(angle), y: CONST_CY + R * Math.sin(angle), scale });
    }
  });
  return out;
}

const shortName = (s) => (s && s.length > 11 ? `${s.slice(0, 10)}…` : (s || ''));

// ── Constellation view ────────────────────────────────────────────────────────
function Constellation({ carried, youName, relLabel, selectedId, setSelectedId }) {
  const { t } = useTranslation('gettingStarted');
  const reduce = prefersReducedMotion();
  const positions = useMemo(() => computeConstellation(carried.length), [carried.length]);

  return (
    <svg className="fl-const-svg" viewBox="0 0 360 360" preserveAspectRatio="xMidYMid meet"
      role="group" aria-label={t('carryConstAria', carried.length)}>
      <defs>
        <radialGradient id="fl-node-glow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#FFF6D8" stopOpacity="0.9" />
          <stop offset="45%" stopColor="#F0A500" stopOpacity="0.55" />
          <stop offset="100%" stopColor="#E8751A" stopOpacity="0" />
        </radialGradient>
      </defs>

      {/* threads (faint, snap on reflow — barely visible) */}
      {positions.map((p, i) => carried[i] && (
        <line key={`t-${carried[i].id}`} className="fl-thread"
          x1={CONST_CX} y1={CONST_CY} x2={p.x} y2={p.y} />
      ))}

      {/* recipient nodes (glide on reflow via transform transition) */}
      {positions.map((p, i) => {
        const c = carried[i];
        if (!c) return null;
        const sel = selectedId === c.id;
        const s = p.scale;
        return (
          <g key={c.id}
            className={`fl-node${sel ? ' sel' : ''}`}
            style={{ transform: `translate(${p.x}px, ${p.y}px)`, transitionDelay: reduce ? '0s' : `${(i % 6) * 20}ms` }}
            role="button" tabIndex={0}
            aria-label={`${c.name} — ${relLabel(c.rel)}`}
            onClick={() => setSelectedId(sel ? null : c.id)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedId(sel ? null : c.id); } }}>
            <circle className="fl-node-glow" r={10.5 * s} fill="url(#fl-node-glow)"
              style={{ animationDelay: reduce ? '0s' : `${(i * 0.37) % 3}s` }} />
            <circle className="fl-node-core" r={3.4 * s} />
            <text className="fl-node-label" y={10.5 * s + 12} fontSize={Math.max(9, 10.5 * s)}>
              {shortName(c.name)}
            </text>
          </g>
        );
      })}

      {/* center "you" node */}
      <g className="fl-node-center" style={{ transform: `translate(${CONST_CX}px, ${CONST_CY}px)` }}>
        <circle className="fl-node-glow" r={19} fill="url(#fl-node-glow)" />
        <circle className="fl-node-core fl-node-core-center" r={6} />
        <text className="fl-node-label fl-node-label-center" y={30} fontSize="12">
          {shortName(youName) || t('you')}
        </text>
      </g>
    </svg>
  );
}

function Carry({ carried, addPerson, release, relSel, setRelSel, labelMap, youName, saving, error, onNext }) {
  const { t } = useTranslation('gettingStarted');
  const [name, setName] = useState('');
  const [view, setView] = useState('constellation');
  const [selectedId, setSelectedId] = useState(null);
  const relLabel = (v) => labelMap[v] || v;
  const selected = carried.find((c) => c.id === selectedId) || null;

  function add() {
    if (!name.trim()) return;
    addPerson(relSel, name.trim());
    setName('');
  }

  return (
    <div className="fl-moment fl-carry">
      <div className="fl-carry-head">
        <div>
          <h2 className="fl-title">{t('carryTitle')}</h2>
          <p className="fl-desc">{t('carryDesc')}</p>
        </div>
        <button type="button" className="fl-view-toggle"
          onClick={() => setView((v) => (v === 'constellation' ? 'list' : 'constellation'))}
          aria-label={view === 'constellation' ? t('viewList') : t('viewConstellation')}
          title={view === 'constellation' ? t('viewList') : t('viewConstellation')}>
          {view === 'constellation' ? <List size={18} /> : <Sparkles size={18} />}
        </button>
      </div>

      {view === 'constellation' ? (
        <div className="fl-const-stage">
          <Constellation carried={carried} youName={youName} relLabel={relLabel}
            selectedId={selectedId} setSelectedId={setSelectedId} />
          <div className="fl-const-caption" aria-live="polite">
            {carried.length === 0 ? (
              <span className="fl-const-empty">{t('carryEmpty')}</span>
            ) : selected ? (
              <>
                <span className="fl-const-cap-name">{selected.name}</span>
                <span className="fl-const-cap-rel">{relLabel(selected.rel)}</span>
                {!selected.existing && (
                  <button type="button" className="fl-const-release"
                    onClick={() => { release(selected.id); setSelectedId(null); }}>
                    {t('releaseShort')} ×
                  </button>
                )}
              </>
            ) : (
              <span className="fl-const-hint">{t('carryConstHint')}</span>
            )}
          </div>
        </div>
      ) : (
        <div className="fl-litany">
          {carried.length === 0 && <div className="fl-empty">{t('carryEmpty')}</div>}
          {carried.map((c) => (
            <Reveal key={c.id} dur={600} y={8} className="fl-litany-row">
              <span className="fl-litany-flame"><Flame size={13} /></span>
              <div className="fl-litany-text">
                <div className="fl-litany-name">{c.name}</div>
                <div className="fl-litany-rel">{relLabel(c.rel)}</div>
              </div>
              {!c.existing && (
                <button type="button" className="fl-release" onClick={() => release(c.id)}
                  aria-label={t('release', c.name)}>×</button>
              )}
            </Reveal>
          ))}
        </div>
      )}

      <div className="fl-composer">
        <div className="fl-chips">
          {RELATIONSHIP_DEFS.map((r) => (
            <button key={r.value} type="button"
              className={`fl-chip${relSel === r.value ? ' sel' : ''}`}
              onClick={() => setRelSel(r.value)}>{relLabel(r.value)}</button>
          ))}
        </div>
        <div className="fl-add-row">
          <input className="fl-name-input" value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') add(); }}
            placeholder={t('carryNamePh', relLabel(relSel))} aria-label={t('carryNamePh', relLabel(relSel))} />
          <button type="button" className="fl-add" onClick={add} disabled={!name.trim()}
            aria-label={t('addPerson')}>+</button>
        </div>
        {error && <div className="fl-error">{error}</div>}
        <button type="button" className="fl-btn fl-btn-block" onClick={onNext} disabled={saving}>
          {carried.length ? t('carryContinue', carried.length) : t('continue')}
        </button>
      </div>
    </div>
  );
}

// ── 3. What season are you in? ───────────────────────────────────────────────--
// One life-situation chip (+ optional detail) seeds an AI prayer request and a
// personalized growth plan, generated in the background after onboarding.
const SEASONS = ['college','spouse','child','grief','habit','newfaith','decision','anxiety','marriage','parenting','work','health','other'];

function Season({ situationKey, setSituationKey, detail, setDetail, error, saving, onNext, onBack }) {
  const { t } = useTranslation('gettingStarted');
  const chosen = !!(situationKey || detail.trim());
  return (
    <div className="fl-moment fl-centered">
      <BackArrow onBack={onBack} label={t('back')} />
      <h2 className="fl-title fl-tc">{t('seasonTitle')}</h2>
      <p className="fl-desc fl-tc fl-narrow">{t('seasonDesc')}</p>
      <div className="fl-season-chips" role="radiogroup" aria-label={t('seasonTitle')}>
        {SEASONS.map((k) => {
          const sel = situationKey === k;
          return (
            <button key={k} type="button" role="radio" aria-checked={sel}
              className={`fl-season-chip${sel ? ' sel' : ''}`}
              onClick={() => setSituationKey(sel ? null : k)}>
              {t(`season_${k}`)}
            </button>
          );
        })}
      </div>
      <textarea className="fl-textarea fl-season-text" rows={2} value={detail}
        onChange={(e) => setDetail(e.target.value)} placeholder={t('seasonOptional')} aria-label={t('seasonOptional')} />
      {error && <div className="fl-error">{error}</div>}
      <button type="button" className="fl-btn fl-btn-wide" onClick={onNext} disabled={saving}>
        {chosen ? t('seasonContinue') : t('seasonSkip')}
      </button>
    </div>
  );
}

// ── 4. When will you come ────────────────────────────────────────────────────--
function Hour({ value, setValue, onNext, onBack }) {
  const { t } = useTranslation('gettingStarted');
  return (
    <div className="fl-moment fl-centered">
      <BackArrow onBack={onBack} label={t('back')} />
      <h2 className="fl-title fl-tc">{t('hourTitle')}</h2>
      <p className="fl-desc fl-tc fl-narrow">{t('hourDesc')}</p>
      <div className="fl-times" role="radiogroup" aria-label={t('hourTitle')}>
        {HOURS.map((h) => {
          const sel = value === h.value;
          return (
            <button key={h.value} type="button" role="radio" aria-checked={sel}
              className={`fl-time${sel ? ' sel' : ''}`} onClick={() => setValue(h.value)}>
              {sel ? <Flame size={13} /> : <span className="fl-time-mark" />}
              {t(`hour_${h.key}`)}
            </button>
          );
        })}
      </div>
      <button type="button" className="fl-btn fl-btn-wide" onClick={onNext} disabled={!value}>
        {t('continue')}
      </button>
    </div>
  );
}

// ── 5. And what is your name ─────────────────────────────────────────────────--
function NameMoment({ firstName, setFirstName, verse, setVerse, error, saving, onNext, onBack }) {
  const { t } = useTranslation('gettingStarted');
  return (
    <div className="fl-moment fl-centered fl-tc">
      <BackArrow onBack={onBack} label={t('back')} />
      <h2 className="fl-title">{t('nameTitle')}</h2>
      <p className="fl-desc fl-narrow">{t('nameDesc')}</p>
      <input className="fl-name-big" autoFocus value={firstName}
        onChange={(e) => setFirstName(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter' && firstName.trim()) onNext(); }}
        placeholder="…" aria-label={t('firstName')} />
      <input className="fl-verse-input" value={verse}
        onChange={(e) => setVerse(e.target.value)}
        placeholder={t('favoriteVersePlaceholder')} aria-label={t('favoriteVerse')} />
      {error && <div className="fl-error">{error}</div>}
      <button type="button" className="fl-btn fl-btn-wide" onClick={onNext}
        disabled={!firstName.trim() || saving}>{t('nameGo')}</button>
    </div>
  );
}

// ── 6. Keep the flame close — native-app promotion ───────────────────────────--
// Same values already hardcoded in AboutScreen.js.
const APP_STORE_URL  = 'https://apps.apple.com/us/app/ignite-faith-growth-prayer/id6761739982';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.theigniteapp.app';

function Install({ onNext, onBack }) {
  const { t } = useTranslation('gettingStarted');
  const { isIOS, isAndroid, isMobile } = detectPlatform();

  const showIOS     = isIOS || !isMobile;
  const showAndroid = isAndroid || !isMobile;

  const IOSCard = () => (
    <div className="fl-app-card">
      <div className="fl-app-card-label">{t('iosCardLabel')}</div>
      <a className="fl-store-btn" href={APP_STORE_URL} target="_blank" rel="noopener noreferrer">
        {t('appStoreCta')}
      </a>
    </div>
  );

  const AndroidCard = () => (
    <div className="fl-app-card">
      <div className="fl-app-card-label">{t('androidCardLabel')}</div>
      <a className="fl-store-btn" href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer">
        {t('playStoreCta')}
      </a>
    </div>
  );

  return (
    <div className="fl-moment fl-centered">
      <BackArrow onBack={onBack} label={t('back')} />
      <div className="fl-flame-lead"><Flame size={20} /></div>
      <h2 className="fl-title fl-tc">{t('installTitle2')}</h2>
      <p className="fl-desc fl-tc fl-narrow">{t('installAppDesc')}</p>

      {!isMobile && <div className="fl-desktop-note">{t('desktopAppNote')}</div>}
      <div className="fl-app-cards">
        {showIOS && <IOSCard />}
        {showAndroid && <AndroidCard />}
      </div>

      <button type="button" className="fl-btn fl-btn-wide" onClick={onNext}>{t('installGo')}</button>
    </div>
  );
}

// ── 7. Go in peace (benediction) ─────────────────────────────────────────────--
function Bless({ carried, firstName, hour, planStarted, finishing, onFinish }) {
  const { t } = useTranslation('gettingStarted');
  const flames = carried.length ? carried.slice(0, 9) : [{ id: 0 }, { id: 1 }, { id: 2 }];
  const time = hour ? clockLabel(hour) : null;
  return (
    <div className="fl-moment fl-centered fl-tc fl-bless">
      <div className="fl-bless-flames">
        {flames.map((c, i) => (
          <span key={c.id ?? i} className="fl-bless-flame"
            style={{ animationDelay: `${i * 0.15}s`, animationDuration: `${2 + (i % 4) * 0.35}s` }}>
            <Flame size={15} />
          </span>
        ))}
      </div>
      <Reveal dur={900}>
        <h1 className="fl-bless-title">{t('blessTitle', firstName.trim())}</h1>
      </Reveal>
      <Reveal dur={900} delay={120}>
        <p className="fl-bless-desc">{t('blessDesc', carried.length, time)}</p>
      </Reveal>
      {planStarted && (
        <Reveal dur={900} delay={240}>
          <p className="fl-bless-plan">{t('blessPlanLine')}</p>
        </Reveal>
      )}
      <button type="button" className="fl-btn-filled" onClick={onFinish} disabled={finishing}>
        {t('blessGo')}
      </button>
    </div>
  );
}

// ── Main: First Light ritual ─────────────────────────────────────────────────--
export default function GettingStartedScreen() {
  const { dbUser, clearOnboarding, needsOnboarding } = useAuth();
  const { t } = useTranslation('gettingStarted');
  const { t: tp } = useTranslation('prayer');
  const navigate = useNavigate();

  // Translated relationship labels (parallel to EN_RELATIONSHIPS order).
  const labelMap = useMemo(() => {
    const translated = tp('relationships');
    const map = {};
    EN_RELATIONSHIPS.forEach((en, i) => {
      map[en] = Array.isArray(translated) ? (translated[i] || en) : en;
    });
    return map;
  }, [tp]);

  const [phase, setPhase] = useState('breath');
  const [carried, setCarried] = useState([]);
  const [relSel, setRelSel] = useState('Mother');
  const [seasonKey, setSeasonKey] = useState(null);
  const [seasonDetail, setSeasonDetail] = useState('');
  const [planStarted, setPlanStarted] = useState(false);
  const [hour, setHour] = useState(null);
  const [firstName, setFirstName] = useState(dbUser?.firstName || '');
  const [verse, setVerse] = useState(dbUser?.favoriteVerse || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [finishing, setFinishing] = useState(false);

  // Inside the native app there's nothing to install, so skip the final moment
  // (name → bless) and drop its progress dot.
  const showInstall = useMemo(() => !isNativeApp(), []);
  const middle = useMemo(
    () => ['carry', 'season', 'hour', 'name', ...(showInstall ? ['install'] : [])],
    [showInstall],
  );

  // Guards so navigating back/forward never double-posts.
  const savedIds = useRef(new Set());
  const seasonSubmitted = useRef(false);
  const loadedRef = useRef(false);

  // Surface any prayer recipients the user already has, so the litany reflects
  // their real list whenever they pass through this moment. These are flagged
  // `existing` and marked already-saved so Continue never re-posts them.
  useEffect(() => {
    if (loadedRef.current) return undefined;
    loadedRef.current = true;
    let cancelled = false;
    (async () => {
      try {
        const res = await prayerApi.list();
        const list = Array.isArray(res?.recipients) ? res.recipients : [];
        if (cancelled || list.length === 0) return;
        const existing = list.map((r) => ({ id: r.id, name: r.name, rel: r.relationship || '', existing: true }));
        existing.forEach((r) => savedIds.current.add(r.id));
        setCarried((prev) => [...existing, ...prev.filter((p) => !p.existing)]);
      } catch { /* non-blocking — onboarding works with an empty list */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Hide the app's bottom nav / chrome for this full-bleed ritual. A body class
  // is bulletproof against stacking-context quirks where the blurred bottom nav
  // (its own backdrop-filter context) paints over a high z-index overlay in the
  // Android WebView regardless of z-index.
  useEffect(() => {
    document.body.classList.add('fl-onboarding');
    return () => document.body.classList.remove('fl-onboarding');
  }, []);

  const addPerson = (rel, name) =>
    setCarried((c) => [...c, { id: Date.now() + Math.random(), rel, name }]);
  // Only freshly-added entries show a release control, so this just drops the
  // local row. Recipients the user already had are left untouched.
  const release = (id) => setCarried((c) => c.filter((x) => x.id !== id));

  function go(next) { setError(''); setPhase(next); }

  // carry → save only the not-yet-persisted recipients, then advance.
  async function saveCarry() {
    const fresh = carried.filter((c) => !savedIds.current.has(c.id));
    if (fresh.length === 0) { go('season'); return; }
    setSaving(true); setError('');
    try {
      await api.post('/api/prayer/bulk', {
        recipients: fresh.map((c) => ({ name: c.name, relationship: c.rel })),
      });
      fresh.forEach((c) => savedIds.current.add(c.id));
      go('season');
    } catch { setError(t('couldNotSave')); }
    finally { setSaving(false); }
  }

  // season → fire (once) the AI prayer request + background growth-plan build,
  // then advance immediately (never block the ritual on AI).
  function saveSeason() {
    if ((seasonKey || seasonDetail.trim()) && !seasonSubmitted.current) {
      seasonSubmitted.current = true;
      setPlanStarted(true);
      api.post('/api/onboarding/season', {
        situationKey:   seasonKey || undefined,
        situationLabel: seasonKey ? t(`season_${seasonKey}`) : undefined,
        detail:         seasonDetail.trim() || undefined,
      }).catch(() => { seasonSubmitted.current = false; });
    }
    go('hour');
  }

  // name → save profile (idempotent PUT). Timezone auto-detected behind the scenes.
  async function saveName() {
    setSaving(true); setError('');
    try {
      await profileApi.update({
        firstName: firstName.trim(),
        lastName: dbUser?.lastName || '',
        bio: dbUser?.bio || '',
        favoriteVerse: verse.trim(),
        timezone: dbUser?.timezone || detectTimezone(),
        prayerNotificationTime: hour || dbUser?.prayerNotificationTime || '',
      });
      go(showInstall ? 'install' : 'bless');
    } catch { setError(t('couldNotSave')); }
    finally { setSaving(false); }
  }

  async function finish() {
    setFinishing(true);
    try { await api.post('/api/prayer/complete-onboarding'); } catch {}
    if (needsOnboarding) clearOnboarding();
    else navigate(-1);
  }

  const midIdx = middle.indexOf(phase);

  return (
    <div className="fl-root">
      <Embers />

      {midIdx >= 0 && (
        <div className="fl-dots" aria-hidden="true">
          {middle.map((m, i) => (
            <span key={m} className={`fl-dot${i <= midIdx ? ' done' : ''}${i === midIdx ? ' current' : ''}`} />
          ))}
        </div>
      )}

      <Reveal key={phase} dur={460} className="fl-stage">
        {phase === 'breath'  && <Breath onNext={() => go('carry')} />}
        {phase === 'carry'   && (
          <Carry carried={carried} addPerson={addPerson} release={release}
            relSel={relSel} setRelSel={setRelSel} labelMap={labelMap}
            youName={firstName.trim() || dbUser?.firstName || ''}
            saving={saving} error={error} onNext={saveCarry} />
        )}
        {phase === 'season'  && (
          <Season situationKey={seasonKey} setSituationKey={setSeasonKey}
            detail={seasonDetail} setDetail={setSeasonDetail}
            error={error} saving={saving} onNext={saveSeason} onBack={() => go('carry')} />
        )}
        {phase === 'hour'    && (
          <Hour value={hour} setValue={setHour} onNext={() => go('name')} onBack={() => go('season')} />
        )}
        {phase === 'name'    && (
          <NameMoment firstName={firstName} setFirstName={setFirstName} verse={verse} setVerse={setVerse}
            error={error} saving={saving} onNext={saveName} onBack={() => go('hour')} />
        )}
        {phase === 'install' && <Install onNext={() => go('bless')} onBack={() => go('name')} />}
        {phase === 'bless'   && (
          <Bless carried={carried} firstName={firstName} hour={hour} planStarted={planStarted}
            finishing={finishing} onFinish={finish} />
        )}
      </Reveal>
    </div>
  );
}
